import { useState, useEffect } from 'react';
import { categories } from '../types/category';
import { pb } from '../lib/pocketbase';
import { useAuth } from '../lib/authTypes';
import { useNavigate } from 'react-router-dom';

interface CreateRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { name: string; detail: string }) => void;
  categoryId: string;
  initialData?: { name: string; detail: string } | null;
  sourceUrl?: string;
  sourceContent?: string;
}

export function CreateRecordModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  categoryId,
  initialData,
  sourceUrl = '',
  sourceContent = ''
}: CreateRecordModalProps) {
  const category = categories.find(c => c.id === categoryId);
  const [name, setName] = useState('');
  const [detail, setDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { checkAuthAndLogout } = useAuth();
  const navigate = useNavigate();

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDetail(initialData.detail);
    }
  }, [initialData]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDetail('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Check if token is still valid before submitting
    const isAuthenticated = checkAuthAndLogout();
    if (!isAuthenticated) {
      setError('Your session has expired. Please log in again.');
      setIsSubmitting(false);
      setTimeout(() => {
        onClose();
        navigate('/login');
      }, 2000);
      return;
    }

    try {
      await pb.collection('facts').create({
        name,
        detail,
        categoryId,
        category: category?.title || '',
        url: sourceUrl,
        content: sourceContent
      });

      onConfirm({ name, detail });
    } catch (error) {
      console.error('Failed to create record:', error);
      
      // Check if error is due to unauthorized access (expired token)
      if (error instanceof Error && error.message.includes('401')) {
        setError('Your session has expired. Please log in again.');
        // Force logout and redirect
        checkAuthAndLogout();
        setTimeout(() => {
          onClose();
          navigate('/login');
        }, 2000);
      } else {
        setError('Failed to create record. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#282828] rounded-lg p-6 max-w-2xl w-full mx-4">
        <h3 className="text-lg font-medium mb-4">Confirm Record Creation</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <div className="w-full bg-[#383838] rounded-lg px-4 py-2 text-gray-400">
              {category?.title || 'No category selected'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter record name"
              className="w-full bg-[#383838] rounded-lg px-4 py-2 text-white"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Detail</label>
            <textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="Enter record details"
              className="w-full bg-[#383838] rounded-lg px-4 py-2 text-white resize-none h-32"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-gray-400 hover:text-white transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-full bg-[#1DB954] text-white hover:bg-[#1ed760] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 